// One live projects list for the whole Dashboard (Brief 4 / P8). Loads
// `GET /projects` ONCE and shares it, so the sidebar's project list, a
// project's sessions header, and a session's assign control all read ONE
// source — and a project created in the sidebar appears everywhere at once.
//
// Projects are the explicit, named entities the orchestrator establishes; the
// immovable goals/spec for each live in its second brain (surfaced at the
// project level). This context is the navigation spine that hangs the sessions
// off those projects.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  createProject,
  renameProject,
  assignProject,
  listProjects,
} from "../../api/client";
import type { ProjectSummary } from "@waytide/core";

interface ProjectsState {
  projects: ProjectSummary[];
  loaded: boolean;
  error: string | null;
  /** Create a project and adopt the refreshed list the create returns. */
  create: (name: string) => Promise<void>;
  /** Rename a project the caller owns; adopts the refreshed list. */
  rename: (projectId: number, name: string) => Promise<void>;
  /** Assign a session to a project; resolves to the assigned project_id. */
  assign: (threadId: number, projectId: number) => Promise<number>;
  /** Re-read the list (e.g. after an out-of-band change). */
  refresh: () => void;
}

const ProjectsCtx = createContext<ProjectsState | null>(null);

export function ProjectsProvider({ children }: { children: ReactNode }) {
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const aliveRef = useRef(true);

  const load = useCallback(() => {
    listProjects()
      .then((res) => {
        if (!aliveRef.current) return;
        setProjects(res.projects);
        setError(null);
        setLoaded(true);
      })
      .catch((e) => {
        if (!aliveRef.current) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoaded(true);
      });
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    load();
    return () => {
      aliveRef.current = false;
    };
  }, [load]);

  const create = useCallback(async (name: string) => {
    const res = await createProject(name);
    if (aliveRef.current) setProjects(res.projects);
  }, []);

  const rename = useCallback(async (projectId: number, name: string) => {
    const res = await renameProject(projectId, name);
    if (aliveRef.current) setProjects(res.projects);
  }, []);

  const assign = useCallback(async (threadId: number, projectId: number) => {
    const ack = await assignProject(threadId, projectId);
    return ack.project_id;
  }, []);

  return (
    <ProjectsCtx.Provider
      value={{ projects, loaded, error, create, rename, assign, refresh: load }}
    >
      {children}
    </ProjectsCtx.Provider>
  );
}

export function useProjectsContext(): ProjectsState {
  const v = useContext(ProjectsCtx);
  if (!v) {
    throw new Error("useProjectsContext must be used within <ProjectsProvider>");
  }
  return v;
}
